// Genera el PDF de una cotización en el navegador (no hay generación de PDF
// en el backend — ver plan del módulo Cotización).
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const TABLA_HEADER_RGB = [68, 81, 94]; // #44515E — gris pizarra, color del encabezado de la tabla

function fmtMoneda(valor) {
  return `$${Number(valor || 0).toLocaleString('es-CL')}`;
}

// La fecha de la cotización es un día, no un instante: "2026-10-20T00:00:00.000Z" no se pasa por Date, porque en
// Chile (UTC−3/−4) se mostraba el día anterior.
function fmtFecha(fecha) {
  if (!fecha) return '';
  const m = String(fecha).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return String(fecha);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Carga la imagen del logo en un <img> para conocer su proporción real y no
// deformarla al insertarla en el PDF.
function cargarDimensionesImagen(dataUrl) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

export async function generarPdfCotizacion(cotizacion, config) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = 210;
  const marginX = 14;

  // ── Encabezado: fondo blanco (muchos logos vienen pensados para eso),
  // el color de marca queda sólo como acento en el texto y la línea ──────
  const alturaLogo = 20;
  if (config?.logo_base64 && config?.logo_mime) {
    const dataUrl = `data:${config.logo_mime};base64,${config.logo_base64}`;
    const dims = await cargarDimensionesImagen(dataUrl);
    const maxH = alturaLogo, maxW = 50;
    let w = maxW, h = maxH;
    if (dims) {
      const ratio = dims.w / dims.h;
      h = maxH;
      w = h * ratio;
      if (w > maxW) { w = maxW; h = w / ratio; }
    }
    const formato = config.logo_mime.includes('png') ? 'PNG' : config.logo_mime.includes('webp') ? 'WEBP' : 'JPEG';
    try {
      doc.addImage(dataUrl, formato, marginX, 10, w, h);
    } catch {
      // Si el formato no es soportado por addImage, seguimos sin logo.
    }
  }

  doc.setTextColor(20, 20, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(`COTIZACIÓN N° ${cotizacion.numero}`, pageW - marginX, 20, { align: 'right' });

  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.25);
  doc.line(marginX, 34, pageW - marginX, 34);

  // ── Datos de la empresa (izquierda) y del cliente (derecha) ───────────
  let y = 44;
  doc.setTextColor(60, 60, 60);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);

  const colEmpresa = [
    config?.nombre_empresa,
    config?.rut,
    config?.direccion ? `${config.direccion}${config?.comuna ? ', ' + config.comuna : ''}` : (config?.comuna || null),
    config?.telefono,
    config?.correo,
  ].filter(Boolean);
  colEmpresa.forEach((linea, i) => doc.text(String(linea), marginX, y + i * 4.6));

  const colCliente = [
    cotizacion.cliente_nombre,
    cotizacion.cliente_rut,
    cotizacion.cliente_giro,
    cotizacion.cliente_direccion ? `${cotizacion.cliente_direccion}${cotizacion.cliente_comuna ? ', ' + cotizacion.cliente_comuna : ''}` : (cotizacion.cliente_comuna || null),
    cotizacion.cliente_correo,
    `Fecha: ${fmtFecha(cotizacion.fecha || cotizacion.creado_en)}`,
  ].filter(Boolean);
  doc.setFont('helvetica', 'bold');
  colCliente.forEach((linea, i) => {
    if (i === 0) { doc.text(String(linea), pageW - marginX, y, { align: 'right' }); doc.setFont('helvetica', 'normal'); }
    else doc.text(String(linea), pageW - marginX, y + i * 4.6, { align: 'right' });
  });

  const filasUsadas = Math.max(colEmpresa.length, colCliente.length);
  y = y + filasUsadas * 4.6 + 6;

  // ── Tabla de líneas ─────────────────────────────────────────────────
  // Autor/Editorial/Descuento se muestran solo si la cotización los incluye
  // — evita dejar esos datos vacíos y feos cuando no se conocen.
  const incluyeAutor = cotizacion.incluye_autor !== false;
  const incluyeEditorial = cotizacion.incluye_editorial !== false;
  const incluyeDescuento = cotizacion.incluye_descuento !== false;
  const lineas = cotizacion.lineas || [];

  const head = ['ISBN', 'Título', 'Cantidad', 'Precio Neto'];
  const columnStyles = {
    0: { cellWidth: 28 },
    2: { halign: 'right', cellWidth: 20 },
    3: { halign: 'right', cellWidth: 28 },
  };
  if (incluyeDescuento) { head.push('DESC%'); columnStyles[head.length - 1] = { halign: 'right', cellWidth: 18 }; }
  head.push('Total');
  columnStyles[head.length - 1] = { halign: 'right', cellWidth: 28 };

  // jspdf-autotable no aplica el halign de columnStyles a las celdas del
  // encabezado de forma confiable (quedaba "Total" pegado a la izquierda
  // mientras los montos de abajo quedaban a la derecha) — se fuerza acá,
  // celda por celda, para cabecera y cuerpo por igual.
  const colsDerecha = new Set(Object.keys(columnStyles).map(Number));
  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    head: [head],
    body: lineas.map(l => {
      const fila = [
        l.isbn || '—',
        [l.titulo, incluyeAutor ? l.autor : null, incluyeEditorial ? l.editorial : null].filter(Boolean).join(' — '),
        String(l.cantidad),
        fmtMoneda(l.precio_neto),
      ];
      if (incluyeDescuento) fila.push(Number(l.descuento_pct) > 0 ? `${Number(l.descuento_pct)}%` : '—');
      fila.push(fmtMoneda(l.total_linea));
      return fila;
    }),
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 2.4, halign: 'left' },
    headStyles: { fillColor: TABLA_HEADER_RGB, textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles,
    didParseCell: data => {
      if (colsDerecha.has(data.column.index)) data.cell.styles.halign = 'right';
    },
  });

  // ── Caja de totales, alineada a la derecha ─────────────────────────
  let yTot = (doc.lastAutoTable?.finalY || y) + 8;
  const totBoxW = 66, totBoxX = pageW - marginX - totBoxW;
  doc.setFontSize(10);
  doc.setTextColor(90, 90, 90);
  doc.setFont('helvetica', 'normal');
  doc.text('Monto Neto', totBoxX, yTot);
  doc.text(fmtMoneda(cotizacion.mnt_neto), pageW - marginX, yTot, { align: 'right' });
  yTot += 6;
  doc.text('IVA 19%', totBoxX, yTot);
  doc.text(fmtMoneda(cotizacion.mnt_iva), pageW - marginX, yTot, { align: 'right' });
  yTot += 2.5;
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.line(totBoxX, yTot, pageW - marginX, yTot);
  yTot += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('Total', totBoxX, yTot);
  doc.text(fmtMoneda(cotizacion.mnt_total), pageW - marginX, yTot, { align: 'right' });

  // ── Validez + datos de depósito ─────────────────────────────────────
  yTot += 14;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(110, 110, 110);
  doc.text('Validez de la cotización: 30 días desde la fecha de emisión.', marginX, yTot);

  if (cotizacion.notas) {
    yTot += 7;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    const notasWrap = doc.splitTextToSize(cotizacion.notas, pageW - marginX * 2);
    doc.text(notasWrap, marginX, yTot);
    yTot += notasWrap.length * 4.4;
  }

  if (cotizacion.datos_deposito) {
    yTot += 8;
    const depositoWrap = doc.splitTextToSize(cotizacion.datos_deposito, pageW - marginX * 2 - 10);
    const boxH = depositoWrap.length * 4.6 + 10;
    doc.setDrawColor(210, 210, 210);
    doc.rect(marginX, yTot, pageW - marginX * 2, boxH);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    doc.text('Depositar:', marginX + 5, yTot + 7);
    doc.setFont('helvetica', 'normal');
    doc.text(depositoWrap, marginX + 5, yTot + 13);
  }

  doc.save(`Cotizacion-${cotizacion.numero}.pdf`);
}
